import React, { useEffect, useState } from 'react';
import { Alert, Button, Input, InputNumber, Modal } from 'antd';
import { decodeHtmlEntities } from '../../pages/orderXV4Utils';

const { TextArea } = Input;

// All ten fields update_items accepts in normalized_parts, in display order.
// `duplicate_group_key` is deliberately excluded — it's a system-managed
// grouping key, not user-facing (see spec-orderxv4-review-edit-extracted-fields.md).
// `required` fields must be non-blank to save (part_number/description are the
// two fields the rest of the app relies on to identify/display this line).
const FIELD_DEFS = [
  { key: 'part_number', label: 'Part Number', required: true },
  { key: 'manufacturer', label: 'Manufacturer' },
  { key: 'manufacturer_part_number', label: 'Manufacturer Part Number' },
  { key: 'customer_part_number', label: 'Customer Part Number' },
  { key: 'description', label: 'Description', type: 'textarea', required: true },
  { key: 'qty', label: 'Quantity', type: 'number', min: 1 },
  { key: 'uom', label: 'UOM' },
  { key: 'notes', label: 'Notes', type: 'textarea' },
  { key: 'input_unit_price', label: 'Unit Price', type: 'number', min: 0 },
  { key: 'input_extended_price', label: 'Extended Price', type: 'number', min: 0 },
];

// Decoded for editing (matches the title's decoded part number) so the same
// text isn't shown HTML-entity-encoded in one place and decoded in another —
// saving persists the decoded, human-readable form going forward.
const buildDraftFromItem = (item) => {
  const parts = item?.normalized_parts || {};
  return {
    part_number: decodeHtmlEntities(parts.part_number || ''),
    manufacturer: decodeHtmlEntities(parts.manufacturer || ''),
    manufacturer_part_number: decodeHtmlEntities(parts.manufacturer_part_number || ''),
    customer_part_number: decodeHtmlEntities(parts.customer_part_number || ''),
    description: decodeHtmlEntities(parts.description || ''),
    qty: parts.qty ?? 1,
    uom: parts.uom || '',
    notes: decodeHtmlEntities(parts.notes || ''),
    input_unit_price: parts.input_unit_price ?? null,
    input_extended_price: parts.input_extended_price ?? null,
  };
};

// Form modal showing and editing every field OrderGenie extracts for a line
// item. Modeled on EditMatchModal.jsx's modal structure (Modal, footer null,
// state reset keyed on `open`/`item?.id`), but for field editing rather than
// match selection.
//
// `onSave(draft)` must resolve only after the save actually succeeds — this
// modal awaits it, and on rejection shows the error inline and stays open
// (no state is assumed to have changed). On success it lets the caller close
// the modal (this component does not close itself).
const EditItemDetailsModal = ({ open, item, onSave, onCancel }) => {
  const [draft, setDraft] = useState(() => buildDraftFromItem(item));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setDraft(buildDraftFromItem(item));
    setError('');
    setSaving(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, item?.id]);

  const handleFieldChange = (key, value) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    const missingRequired = FIELD_DEFS.filter(
      (field) => field.required && !String(draft[field.key] || '').trim()
    );
    if (missingRequired.length > 0) {
      setError(`${missingRequired.map((f) => f.label).join(', ')} cannot be blank.`);
      return;
    }
    setSaving(true);
    setError('');
    try {
      // Always submits the full current value of all ten fields (edited or
      // not) — never a partial payload — since update_items has no
      // preserve-on-omit fallback for part_number/manufacturer_part_number/
      // customer_part_number/manufacturer/description.
      await onSave?.({ ...draft });
    } catch (err) {
      setError(err?.message || 'Unable to save item');
      setSaving(false);
      return;
    }
    setSaving(false);
  };

  const partNumber = decodeHtmlEntities(item?.normalized_parts?.part_number || '');

  return (
    <Modal
      open={open}
      onCancel={saving ? undefined : onCancel}
      footer={null}
      title={`Edit Details${partNumber ? ` — ${partNumber}` : ''}`}
      width={680}
      destroyOnClose
      maskClosable={!saving}
    >
      {error && <Alert type="error" showIcon message={error} className="mb-3" />}
      <div className="row g-3">
        {FIELD_DEFS.map((field) => {
          const inputId = `edit-item-field-${field.key}`;
          return (
            <div className="col-12 col-md-6" key={field.key}>
              <label className="form-label small" htmlFor={inputId}>
                {field.label}{field.required && ' *'}
              </label>
              {field.type === 'textarea' ? (
                <TextArea
                  id={inputId}
                  rows={2}
                  value={draft[field.key]}
                  onChange={(e) => handleFieldChange(field.key, e.target.value)}
                  disabled={saving}
                />
              ) : field.type === 'number' ? (
                <InputNumber
                  id={inputId}
                  style={{ width: '100%' }}
                  min={field.min}
                  value={draft[field.key]}
                  onChange={(val) => handleFieldChange(field.key, val)}
                  disabled={saving}
                />
              ) : (
                <Input
                  id={inputId}
                  value={draft[field.key]}
                  onChange={(e) => handleFieldChange(field.key, e.target.value)}
                  disabled={saving}
                />
              )}
            </div>
          );
        })}
      </div>
      <div className="d-flex justify-content-end gap-2 mt-4">
        <Button onClick={onCancel} disabled={saving}>Cancel</Button>
        <Button type="primary" onClick={handleSave} loading={saving}>Save</Button>
      </div>
    </Modal>
  );
};

export default EditItemDetailsModal;
